import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { LogService } from 'projects/com-logging/src/lib/log.service';
import { of, switchMap } from 'rxjs';
import { ApplicationStateService, BookingService } from 'src/app/core';
import { GetVirtualGiftcardResult } from 'src/app/core/modules/payment';
import { DateHelper } from 'src/app/helpers';

@Component({
  selector: 'app-giftcard-link',
  templateUrl: './giftcard-link.component.html',
})
export class GiftcardLinkComponent implements OnInit {
  public form: FormGroup = new FormGroup({
    giftcardGuid: new FormControl(''),
    passphrase: new FormControl(''),
  });
  private giftcard?: GetVirtualGiftcardResult;
  private link?: string;
  public get ShowGiftcard() {
    return this.giftcard;
  }
  public get GiftCardNumber() {
    return this.giftcard?.Number;
  }
  public get GiftCardAmount() {
    return this.giftcard?.InitialAmount ?? 0;
  }
  public get GiftCardCreated() {
    if (!this.giftcard?.DateOfPurchase) return undefined;
    const date = new Date(this.giftcard.DateOfPurchase);
    return DateHelper.toServerDateFormatString(date);
  }
  public get GiftCardName() {
    return this.giftcard?.Name;
  }
  public get GiftCardPdfLink() {
    return this.link;
  }
  public get CanOpenPdf() {
    return this.giftcard && this.giftcard.RemainingAmount > 0 && this.giftcard.IsActive;
  }
  public get HotelCurrency() {
    return this.applicationState.HotelCurrency;
  }
  constructor(
    private log: LogService,
    private booking: BookingService,
    private router: Router,
    private route: ActivatedRoute,
    private applicationState: ApplicationStateService,
  ) {}

  public get giftcardGuid() {
    return this.form.get('giftcardGuid')?.value;
  }

  public openGiftCardLink() {
    this.log.debug('Opening giftcard link', this.link);
    window.open(this.link, '_blank');
  }

  async ngOnInit(): Promise<void> {
    this.route.paramMap
      .pipe(switchMap((params: ParamMap) => of(params.get('giftcardGuid'))))
      .subscribe(async (giftcardGuid) => {
        const regex = /^[{]?[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}[}]?$/;
        if (!giftcardGuid || !giftcardGuid?.match(regex)) {
          this.router.navigate(['']);
          return;
        }
        this.form.patchValue({ giftcardGuid });
        await this.onSubmit();
      });
    this.route.queryParamMap
      .pipe(switchMap((params: ParamMap) => of(params.get('passphrase'))))
      .subscribe(async (passphrase) => {
        if (passphrase) {
          this.form.patchValue({ passphrase });
          await this.onSubmit();
        }
      });
  }
  async loadGiftcard(giftcardGuid: string, passphrase: string): Promise<void> {
    this.log.debug('Loading giftcard', giftcardGuid, passphrase);
    if (!giftcardGuid || !passphrase) return;
    try {
      const giftcard = await this.booking.getVirtualGiftCard(giftcardGuid, passphrase);
      const link = await this.booking.GenerateVirtualGiftCardLink(giftcardGuid, passphrase);
      this.giftcard = giftcard;
      this.link = link;
      this.log.debug('Completed loading giftcard', giftcard, link);
    } catch (e) {
      if (e instanceof HttpErrorResponse) {
        if (e.status === 400) {
          this.form.setErrors({ unauthorized: true });
        } else if (e.status === 401) {
          this.form.setErrors({ badRequest: true });
        } else if (e.status === 404) {
          this.form.setErrors({ notFound: true });
        } else if (e.status === 500) {
          this.form.setErrors({ internalError: true });
        } else {
          this.form.setErrors({ unknownError: true });
        }
      }
      this.log.warn('Error getting giftcard', e);
    }
  }
  async onSubmit(): Promise<void> {
    const giftcardGuid = this.form.controls.giftcardGuid.value;
    const passphrase = this.form.controls.passphrase.value;
    if (!giftcardGuid) return;
    await this.loadGiftcard(giftcardGuid, passphrase);
  }
}
